// ============================================================
// MySQLLearningJourney.jsx
//
// A branded, responsive course content page for the
// "Learning Journey & Database Fundamentals (MySQL)" lesson.
//
// Brand palette (same spirit as your standard):
//   Background  → #100720  (deep purple-black)
//   Primary     → goldenrod / #DAA520
//   Accent      → #FFD700  (gold highlights)
//   Text        → #FFFFFF  (white)
//   Surface     → rgba(255,255,255,0.04) with gold borders
//
// Built with:
//   - Material UI (MUI v5) for layout, typography & icons
//   - Fully responsive (xs → xl)
//   - Commented throughout for junior developers
// ============================================================

import React, { useState } from "react";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Collapse,
    Paper,
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import MenuBookIcon from "@mui/icons-material/MenuBook";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LinkIcon from "@mui/icons-material/Link";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import StorageIcon from "@mui/icons-material/Storage";
import LayersIcon from "@mui/icons-material/Layers";
import CodeIcon from "@mui/icons-material/Code";
import SchoolIcon from "@mui/icons-material/School";
import StarIcon from "@mui/icons-material/Star";
import SchemaIcon from "@mui/icons-material/Schema";
import TableChartIcon from "@mui/icons-material/TableChart";
import DataObjectIcon from "@mui/icons-material/DataObject";
import SpeedIcon from "@mui/icons-material/Speed";
import SecurityIcon from "@mui/icons-material/Security";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg: "#100720",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold: "#DAA520",
    goldBright: "#FFD700",
    goldGlow: "rgba(218,165,32,0.15)",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.60)",
    codeBg: "rgba(0,0,0,0.45)",
    codeText: "#FFD700",
};

// ── Reusable section-heading style ────────────────────────
const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ============================================================
//  SUB-COMPONENT: SectionTitle
// ============================================================
function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box
                sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    background: BRAND.goldGlow,
                    border: `1px solid ${BRAND.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                }}
            >
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>

            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ConceptCard
// ============================================================
function ConceptCard({ icon, title, description }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                height: "100%",
                background: BRAND.surface,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 3,
                transition: "border-color 0.25s, box-shadow 0.25s",
                "&:hover": {
                    borderColor: BRAND.borderHover,
                    boxShadow: `0 0 20px ${BRAND.goldGlow}`,
                },
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.2 }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 22 } })}
                <Typography
                    sx={{
                        fontFamily: "'Georgia', serif",
                        fontWeight: 700,
                        color: BRAND.white,
                        fontSize: 14,
                    }}
                >
                    {title}
                </Typography>
            </Box>

            <Typography sx={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.65 }}>
                {description}
            </Typography>
        </Paper>
    );
}

// ============================================================
//  SUB-COMPONENT: CodeBlock
//  Displays commands/SQL with a copy button.
//  Props:
//    lines – array of { cmd, comment } objects
// ============================================================
function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box
            sx={{
                background: BRAND.codeBg,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 2,
                overflow: "hidden",
                mb: 2,
            }}
        >
            <Box
                sx={{
                    px: 2,
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: `1px solid ${BRAND.border}`,
                    background: "rgba(0,0,0,0.3)",
                }}
            >
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>

                <Tooltip title={copied ? "Copied!" : "Copy commands"} placement="top">
                    <Box
                        onClick={handleCopy}
                        sx={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            color: copied ? BRAND.goldBright : BRAND.muted,
                            fontSize: 12,
                            transition: "color 0.2s",
                            "&:hover": { color: BRAND.gold },
                        }}
                    >
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>

            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && (
                            <Typography
                                sx={{
                                    fontFamily: "monospace",
                                    fontSize: 12,
                                    color: "rgba(255,255,255,0.32)",
                                    mb: 0.3,
                                }}
                            >
                                -- {comment}
                            </Typography>
                        )}

                        <Typography
                            sx={{
                                fontFamily: "monospace",
                                fontSize: { xs: 13, md: 14 },
                                color: BRAND.codeText,
                                lineHeight: 1.7,
                                whiteSpace: "pre-wrap",
                            }}
                        >
                            {cmd}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ExpandableExample
// ============================================================
function ExpandableExample({ title, icon, children }) {
    const [open, setOpen] = useState(false);

    return (
        <Box
            sx={{
                border: `1px solid ${open ? BRAND.borderHover : BRAND.border}`,
                borderRadius: 2.5,
                overflow: "hidden",
                mb: 1.5,
                transition: "border-color 0.25s",
            }}
        >
            <Box
                onClick={() => setOpen((prev) => !prev)}
                sx={{
                    px: 2.5,
                    py: 1.8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    background: open ? BRAND.goldGlow : BRAND.surface,
                    transition: "background 0.25s",
                    "&:hover": { background: BRAND.goldGlow },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
                    <Typography sx={{ color: BRAND.white, fontWeight: 600, fontSize: 14 }}>
                        {title}
                    </Typography>
                </Box>

                {open ? <ExpandLessIcon sx={{ color: BRAND.gold }} /> : <ExpandMoreIcon sx={{ color: BRAND.muted }} />}
            </Box>

            <Collapse in={open}>
                <Box
                    sx={{
                        px: 2.5,
                        py: 2,
                        background: "rgba(0,0,0,0.25)",
                        borderTop: `1px solid ${BRAND.border}`,
                    }}
                >
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ResourceLink
// ============================================================
function ResourceLink({ href, label, sub }) {
    return (
        <Box
            component="a"
            href={href}
            target="_blank"
            rel="noreferrer"
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2,
                mb: 1.2,
                textDecoration: "none",
                border: `1px solid ${BRAND.border}`,
                borderRadius: 2,
                background: BRAND.surface,
                transition: "border-color 0.25s, box-shadow 0.25s",
                "&:hover": {
                    borderColor: BRAND.borderHover,
                    boxShadow: `0 0 16px ${BRAND.goldGlow}`,
                },
            }}
        >
            <LinkIcon sx={{ color: BRAND.gold, fontSize: 20, flexShrink: 0 }} />

            <Box>
                <Typography sx={{ color: BRAND.gold, fontWeight: 700, fontSize: 14, lineHeight: 1.3 }}>
                    {label}
                </Typography>
                {sub && (
                    <Typography sx={{ color: BRAND.muted, fontSize: 12, mt: 0.3 }}>
                        {sub}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

// ============================================================
//  MAIN COMPONENT: MySQLLearningJourney
// ============================================================
export default function MySQLLearningJourney() {
    return (
        <Box
            sx={{
                minHeight: "100vh",
                background: BRAND.bg,
                backgroundImage: `repeating-linear-gradient(
          -45deg,
          rgba(218,165,32,0.02) 0px,
          rgba(218,165,32,0.02) 1px,
          transparent 1px,
          transparent 12px
        )`,
                py: { xs: 4, md: 7 },
                px: { xs: 2, sm: 3 },
            }}
        >
            <Container maxWidth="lg">
                {/* ══════════════════════════════════════════════
            HERO HEADER
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip
                        icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />}
                        label="Foundation · Module 01"
                        sx={{
                            mb: 2.5,
                            background: BRAND.goldGlow,
                            border: `1px solid ${BRAND.border}`,
                            color: BRAND.gold,
                            fontFamily: "monospace",
                            fontSize: 11,
                            letterSpacing: 1,
                            textTransform: "uppercase",
                        }}
                    />

                    <Typography
                        variant="h3"
                        sx={{
                            ...sectionHeading,
                            fontSize: { xs: 26, sm: 34, md: 44 },
                            mb: 2,
                            "& span": {
                                background: `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldBright})`,
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                            },
                        }}
                    >
                        Learning Journey & <span>Database Fundamentals</span> (MySQL)
                    </Typography>

                    <Typography
                        sx={{
                            color: BRAND.muted,
                            fontSize: { xs: 14, md: 16 },
                            maxWidth: 720,
                            lineHeight: 1.75,
                            mx: { xs: "auto", md: 0 },
                        }}
                    >
                        Databases are where real applications store truth. In this lesson, you’ll learn what a database is,
                        how relational data is organised, and how MySQL + SQL help you create, query, and protect data
                        like a professional.
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 2,
                            mt: 3,
                            justifyContent: { xs: "center", md: "flex-start" },
                        }}
                    >
                        {[
                            { label: "~2 hours", sub: "read time" },
                            { label: "Beginner", sub: "level" },
                            { label: "8 topics", sub: "covered" },
                        ].map(({ label, sub }) => (
                            <Box
                                key={label}
                                sx={{
                                    textAlign: "center",
                                    background: BRAND.surface,
                                    border: `1px solid ${BRAND.border}`,
                                    borderRadius: 2,
                                    px: 2.5,
                                    py: 1.2,
                                }}
                            >
                                <Typography sx={{ color: BRAND.goldBright, fontWeight: 800, fontSize: 16 }}>
                                    {label}
                                </Typography>
                                <Typography sx={{ color: BRAND.muted, fontSize: 11 }}>{sub}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* ══════════════════════════════════════════════
            SECTION 1 – WHAT IS A DATABASE & MYSQL?
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<LightbulbIcon />} label="What Is a Database (and MySQL)?" />

                    <Typography sx={{ color: BRAND.muted, lineHeight: 1.85, mb: 2, fontSize: { xs: 13, md: 15 } }}>
                        A <strong style={{ color: BRAND.white }}>database</strong> is a structured system for storing, organizing,
                        and retrieving information reliably. In real apps—banking, e‑commerce, learning platforms—databases
                        keep data consistent even when thousands of users act at the same time.
                    </Typography>

                    <Typography sx={{ color: BRAND.muted, lineHeight: 1.85, fontSize: { xs: 13, md: 15 } }}>
                        <strong style={{ color: BRAND.white }}>MySQL</strong> is a popular relational database management system
                        (RDBMS). It stores data in tables (rows + columns) and uses <strong style={{ color: BRAND.white }}>SQL</strong> (Structured Query Language)
                        to define, read, and update information. If you master MySQL fundamentals, you build a strong base for backend development,
                        analytics, and production database work.
                    </Typography>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 2 – KEY CONCEPTS
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<MenuBookIcon />} label="Key Concepts (Database Foundations)" />

                    <Grid container spacing={2}>
                        {[
                            {
                                icon: <StorageIcon />,
                                title: "Database vs. Table",
                                description:
                                    "A database is the container. Tables live inside it. Tables store records (rows) with attributes (columns). Good naming + structure makes everything easier later.",
                            },
                            {
                                icon: <TableChartIcon />,
                                title: "Rows, Columns & Keys",
                                description:
                                    "Rows represent individual records. Columns represent fields. A PRIMARY KEY uniquely identifies each row. FOREIGN KEYS connect tables safely.",
                            },
                            {
                                icon: <SchemaIcon />,
                                title: "Relationships (1–1, 1–Many, Many–Many)",
                                description:
                                    "Relational databases shine when data is connected. You model relationships with foreign keys and junction tables (for many‑to‑many).",
                            },
                            {
                                icon: <CodeIcon />,
                                title: "SQL (DDL vs DML)",
                                description:
                                    "DDL defines structure (CREATE/ALTER/DROP). DML manipulates data (SELECT/INSERT/UPDATE/DELETE). You need both to build real systems.",
                            },
                            {
                                icon: <SpeedIcon />,
                                title: "Indexes & Performance",
                                description:
                                    "Indexes speed up reads by creating lookup structures. Used well: fast queries. Used poorly: slow writes and wasted space. Learn EXPLAIN early.",
                            },
                            {
                                icon: <LayersIcon />,
                                title: "Transactions & Consistency",
                                description:
                                    "Transactions group operations so they succeed or fail together. ACID properties protect your data during failures and concurrent users.",
                            },
                            {
                                icon: <DataObjectIcon />,
                                title: "Normalization (Clean Design)",
                                description:
                                    "Normalization reduces duplication and anomalies. Start normalized, then denormalize only when performance or reporting needs it.",
                            },
                            {
                                icon: <SecurityIcon />,
                                title: "Security & Permissions",
                                description:
                                    "Databases are valuable targets. You must understand least privilege (GRANT/REVOKE), strong credentials, and safe query practices.",
                            },
                        ].map((card) => (
                            <Grid item xs={12} sm={6} md={3} key={card.title}>
                                <ConceptCard {...card} />
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 3 – TRY IT YOURSELF
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<CodeIcon />} label="Try It Yourself (SQL Starter Lab)" />

                    <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 3, lineHeight: 1.75 }}>
                        Use your local MySQL client, MySQL Workbench, or an online SQL playground. Run these statements in order.
                        Read the results carefully—SQL becomes easy when you learn to predict output.
                    </Typography>

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>
                        🟡 1) Create a Database & Table
                    </Typography>
                    <CodeBlock
                        lines={[
                            { comment: "Create and select a database", cmd: "CREATE DATABASE academy;\nUSE academy;" },
                            {
                                comment: "Create a simple Students table",
                                cmd: `CREATE TABLE students (
  id INT PRIMARY KEY AUTO_INCREMENT,
  full_name VARCHAR(100) NOT NULL,
  email VARCHAR(120) UNIQUE,
  created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP
);`,
                            },
                        ]}
                    />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>
                        🟡 2) Insert Data & Query It
                    </Typography>
                    <CodeBlock
                        lines={[
                            {
                                comment: "Insert sample rows",
                                cmd: `INSERT INTO students (full_name, email) VALUES
('Ada Lovelace', 'ada@academy.com'),
('Alan Turing', 'alan@academy.com'),
('Grace Hopper', 'grace@academy.com');`,
                            },
                            {
                                comment: "Read the data",
                                cmd: "SELECT id, full_name, email, created_at FROM students ORDER BY id;",
                            },
                            {
                                comment: "Filter results",
                                cmd: "SELECT full_name FROM students WHERE email LIKE '%@academy.com';",
                            },
                        ]}
                    />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 2, mt: 4 }}>
                        🔽 Deeper Examples (click to expand)
                    </Typography>

                    {/* Accordion 1: Relationships + Joins */}
                    <ExpandableExample title="Model relationships + JOIN queries" icon={<SchemaIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.75 }}>
                            Real apps use multiple tables. Below we add a <strong style={{ color: BRAND.white }}>courses</strong> table and enrollments.
                            The JOIN connects data across tables.
                        </Typography>

                        <CodeBlock
                            lines={[
                                {
                                    comment: "Create Courses + Enrollments (many-to-many)",
                                    cmd: `CREATE TABLE courses (
  id INT PRIMARY KEY AUTO_INCREMENT,
  title VARCHAR(80) NOT NULL
);

CREATE TABLE enrollments (
  student_id INT NOT NULL,
  course_id INT NOT NULL,
  PRIMARY KEY (student_id, course_id),
  FOREIGN KEY (student_id) REFERENCES students(id),
  FOREIGN KEY (course_id) REFERENCES courses(id)
);`,
                                },
                                {
                                    comment: "Seed courses and enrollments",
                                    cmd: `INSERT INTO courses (title) VALUES ('SQL Basics'), ('Indexing & Performance');

INSERT INTO enrollments (student_id, course_id) VALUES
(1, 1),
(2, 1),
(2, 2);`,
                                },
                                {
                                    comment: "JOIN across 3 tables",
                                    cmd: `SELECT s.full_name, c.title
FROM students s
JOIN enrollments e ON e.student_id = s.id
JOIN courses c ON c.id = e.course_id
ORDER BY s.full_name;`,
                                },
                            ]}
                        />
                    </ExpandableExample>

                    {/* Accordion 2: Index + EXPLAIN */}
                    <ExpandableExample title="Performance: create an index + inspect query plan" icon={<SpeedIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.75 }}>
                            Indexes speed up lookups. <strong style={{ color: BRAND.white }}>EXPLAIN</strong> helps you understand how MySQL runs a query.
                        </Typography>

                        <CodeBlock
                            lines={[
                                {
                                    comment: "Add an index (useful for searches by email)",
                                    cmd: "CREATE INDEX idx_students_email ON students(email);",
                                },
                                {
                                    comment: "Inspect how the query is executed",
                                    cmd: "EXPLAIN SELECT * FROM students WHERE email = 'alan@academy.com';",
                                },
                            ]}
                        />
                    </ExpandableExample>

                    {/* Accordion 3: Transactions */}
                    <ExpandableExample title="Transactions: COMMIT vs ROLLBACK" icon={<LayersIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.75 }}>
                            Transactions ensure consistency. If something goes wrong mid‑operation, you can roll back instead of leaving partial updates.
                        </Typography>

                        <CodeBlock
                            lines={[
                                { comment: "Start a transaction", cmd: "START TRANSACTION;" },
                                { comment: "Make changes", cmd: "UPDATE students SET full_name = 'Alan M. Turing' WHERE id = 2;" },
                                { comment: "Undo the changes (practice safety)", cmd: "ROLLBACK;" },
                                { comment: "Try again and commit when confident", cmd: "START TRANSACTION;\nUPDATE students SET full_name = 'Alan M. Turing' WHERE id = 2;\nCOMMIT;" },
                            ]}
                        />
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 4 – WHAT YOU HAVE LEARNED
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="What You've Learned" />

                    <Grid container spacing={1.5}>
                        {[
                            "What a database is and why it’s critical for real applications",
                            "How MySQL stores data using tables, rows, columns, and keys",
                            "How SQL is grouped into DDL (structure) and DML (data)",
                            "How to create a database and tables with sensible constraints",
                            "How to insert and query data using SELECT + filters",
                            "How relationships work and how JOIN brings tables together",
                            "Why indexes matter and how EXPLAIN helps performance thinking",
                            "How transactions protect your data integrity (ACID basics)",
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1, flexShrink: 0 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.6 }}>
                                        {item}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 5 – RESOURCES
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 4 }}>
                    <SectionTitle icon={<LinkIcon />} label="Further Resources" />

                    <ResourceLink
                        href="https://dev.mysql.com/doc/"
                        label="MySQL Official Documentation"
                        sub="The authoritative reference for SQL, functions, storage engines, performance, and administration."
                    />
                    <ResourceLink
                        href="https://sqlbolt.com/"
                        label="SQLBolt (Interactive SQL Lessons)"
                        sub="Fast, hands‑on practice for SQL fundamentals with immediate feedback."
                    />
                    <ResourceLink
                        href="https://www.mysqltutorial.org/"
                        label="MySQL Tutorial"
                        sub="Beginner-friendly guides covering SQL, joins, indexes, and real examples."
                    />
                    <ResourceLink
                        href="https://use-the-index-luke.com/"
                        label="Use The Index, Luke!"
                        sub="Best-practice explanations for indexing and query performance (highly recommended)."
                    />
                </Box>

                {/* Footer note */}
                <Box
                    sx={{
                        mt: 2,
                        p: 2.5,
                        border: `1px solid ${BRAND.border}`,
                        borderRadius: 2,
                        background: BRAND.goldGlow,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                    }}
                >
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.7 }}>
                        <strong style={{ color: BRAND.white }}>Pro tip for juniors:</strong> Don’t rush into “advanced SQL.”
                        First master clean table design (keys + constraints), then JOINs, then indexing, then transactions.
                        That order prevents 80% of real‑world database mistakes.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
}
